<!--
Copyright (C) 2024 Checkmk GmbH - License: GNU General Public License v2
This file is part of Checkmk (https://checkmk.com). It is subject to the terms and
conditions defined in the file COPYING, which is part of this source code package.
-->
<script setup lang="ts">
import type { TranslatedString } from '@/lib/i18nString'

export type ToggleButtonOption = {
  label: string
  value: string
  tooltip?: TranslatedString | undefined
  disabled?: boolean | string | undefined
  disabledTooltip?: TranslatedString | undefined
}

export interface ToggleButtonGroupProps {
  options: ToggleButtonOption[]
  modelValue?: string | null
}

const props = defineProps<ToggleButtonGroupProps>()

const emit = defineEmits({
  'update:modelValue': (_value: string) => true
})

const isSelected = (value: string) => props.modelValue !== null && value === props.modelValue
const isDisabled = (disabled: boolean | string | undefined) =>
  disabled === true || disabled === 'true'
function setSelectedOption(value: string) {
  emit('update:modelValue', value)
}
</script>

<template>
  <div class="toggle_buttons_container">
    <button
      v-for="option in options"
      :key="option.value"
      type="button"
      class="toggle_option"
      :class="{
        selected: isSelected(option.value),
        disabled: isDisabled(option.disabled)
      }"
      :aria-label="`Toggle ${option.label}`"
      :disabled="isDisabled(option.disabled)"
      :title="isDisabled(option.disabled) ? option.disabledTooltip : option.tooltip"
      @click.prevent="setSelectedOption(option.value)"
    >
      {{ option.label }}
    </button>
  </div>
</template>

<style scoped>
/* stylelint-disable-next-line checkmk/vue-bem-naming-convention */
.toggle_buttons_container {
  width: max-content;
  max-width: 100%;
  margin-bottom: 8px;
  padding: 5px;
  border-radius: 5px;
  border: 1px solid var(--toggle-button-group-border-color);
  background-color: var(--toggle-button-group-inactive-bg-color);
  display: flex;
  flex-wrap: wrap;
}

/* stylelint-disable-next-line checkmk/vue-bem-naming-convention */
.toggle_option {
  height: auto;
  min-width: 150px;
  border: none;
  background-color: var(--toggle-button-group-inactive-bg-color);
  color: var(--toggle-button-group-inactive-text-color);
  margin: 0 2px;
  padding: 3px;
  font-weight: var(--font-weight-default);
}

/* stylelint-disable-next-line checkmk/vue-bem-naming-convention */
.toggle_option:is(:disabled) {
  color: var(--toggle-button-group-disabled-text-color);
  cursor: not-allowed;
  background-color: var(--toggle-button-group-inactive-bg-color);
}

/* stylelint-disable-next-line checkmk/vue-bem-naming-convention */
.toggle_option:is(:disabled:hover) {
  background-color: var(--toggle-button-group-inactive-bg-color);
}

/* stylelint-disable-next-line checkmk/vue-bem-naming-convention */
.toggle_option:hover:not(:is(.selected, .disabled)) {
  background-color: rgb(from var(--toggle-button-group-hover-bg-color) r g b / 60%);
}

/* stylelint-disable-next-line checkmk/vue-bem-naming-convention */
.selected {
  border: 1px solid var(--toggle-button-group-border-color);
  background-color: var(--toggle-button-group-active-bg-color);
  color: var(--toggle-button-group-active-text-color);
  font-weight: var(--font-weight-bold);
}
</style>
